import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  FileText,
  DollarSign,
  Clock,
  Users,
  BarChart4,
  PieChart,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
} from "lucide-react";
import {
  PieChart as RechartPie,
  Pie,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { getToken, base_url } from "../../config/config";

const ProjectFinancialDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [laborSummary, setLaborSummary] = useState(null);
  const [costBreakdown, setCostBreakdown] = useState([]);
  const [laborChartData, setLaborChartData] = useState([]);

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

  useEffect(() => {
    fetchProjectFinancials();
  }, [id]);

  const fetchProjectFinancials = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${base_url}/v1/project-financial-details/${id}`,
      );
      const { projectData, financialSummary, laborSummary, costBreakdown } =
        response.data;

      setProjectData(projectData);
      setFinancialSummary(financialSummary);
      setLaborSummary(laborSummary);
      setCostBreakdown(costBreakdown);

      // Process labor data for time-based chart
      if (laborSummary && laborSummary.details) {
        // Group labor entries by date
        const laborByDate = {};

        laborSummary.details.forEach((entry) => {
          const date = new Date(entry.startTime).toISOString().split("T")[0];

          if (!laborByDate[date]) {
            laborByDate[date] = {
              date,
              hours: 0,
              cost: 0,
              entries: 0,
            };
          }

          laborByDate[date].hours += entry.duration;
          laborByDate[date].cost += entry.cost;
          laborByDate[date].entries += 1;
        });

        // Convert to array for chart
        const chartData = Object.values(laborByDate).sort(
          (a, b) => new Date(a.date) - new Date(b.date),
        );

        setLaborChartData(chartData);
      }

      setLoading(false);
    } catch (err) {
      console.error("Error fetching project financials:", err);
      setError("Failed to load project financial data. Please try again.");
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
    }).format(value);
  };

  if (loading)
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto'></div>
          <p className='mt-3 text-gray-600'>Loading financial data...</p>
        </div>
      </div>
    );

  if (error)
    return (
      <div className='bg-red-50 border-l-4 border-red-500 p-4 m-4'>
        <div className='flex'>
          <div className='flex-shrink-0'>
            <AlertTriangle className='h-5 w-5 text-red-500' />
          </div>
          <div className='ml-3'>
            <p className='text-red-700'>{error}</p>
          </div>
        </div>
      </div>
    );

  if (!projectData || !financialSummary)
    return (
      <div className='bg-yellow-50 border-l-4 border-yellow-500 p-4 m-4'>
        <div className='flex'>
          <div className='flex-shrink-0'>
            <AlertTriangle className='h-5 w-5 text-yellow-500' />
          </div>
          <div className='ml-3'>
            <p className='text-yellow-700'>
              No financial data available for this project.
            </p>
          </div>
        </div>
      </div>
    );

  const missingFields = [];
  if (!projectData.totalMaterialCost) missingFields.push("Material Cost");
  if (!projectData.totalsubcontratorCost)
    missingFields.push("Subcontractor Cost");
  if (!projectData.SaleCost) missingFields.push("Sale Cost (Revenue)");
  if (!projectData.MengeCost) missingFields.push("Hourly Rate");

  return (
    <div className='bg-gray-50 min-h-screen'>
      <div className='container mx-auto px-4 py-6'>
        {/* Page Header */}
        <div className='flex items-center mb-6'>
          <button
            onClick={() => navigate("/financials/profit-loss")}
            className='mr-4 p-2 bg-white rounded-full shadow hover:bg-gray-100'>
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className='text-2xl font-bold'>Project Financial Details</h1>
            <p className='text-gray-500'>
              {projectData.Auftragsbezeichnung || "Unnamed Project"}
              {projectData.ANR && ` - ${projectData.ANR}`}
            </p>
          </div>
        </div>

        {/* Missing Fields Warning */}
        {missingFields.length > 0 && (
          <div className='bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded-r-md'>
            <div className='flex'>
              <div className='flex-shrink-0'>
                <AlertTriangle className='h-5 w-5 text-yellow-500' />
              </div>
              <div className='ml-3'>
                <p className='text-yellow-700 font-medium'>
                  Missing financial information
                </p>
                <p className='text-sm text-yellow-600 mt-1'>
                  This project is missing {missingFields.join(", ")}. The
                  financial analysis may be incomplete or inaccurate.
                </p>
                <div className='mt-2'>
                  <button
                    onClick={() => navigate(`/projects/edit/${id}`)}
                    className='inline-flex items-center text-sm text-yellow-800 bg-yellow-100 px-3 py-1 rounded-md hover:bg-yellow-200'>
                    Update Project Data
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Financial Summary Cards */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6'>
          <div className='bg-white p-4 rounded-lg shadow'>
            <div className='flex justify-between items-start mb-2'>
              <h3 className='text-gray-500 text-sm'>Total Revenue</h3>
              <DollarSign className='text-blue-500' size={20} />
            </div>
            <p className='text-2xl font-bold mb-1'>
              {formatCurrency(financialSummary.revenue)}
            </p>
            {!projectData.SaleCost && (
              <p className='text-xs text-red-500'>Missing revenue data</p>
            )}
          </div>

          <div className='bg-white p-4 rounded-lg shadow'>
            <div className='flex justify-between items-start mb-2'>
              <h3 className='text-gray-500 text-sm'>Total Cost</h3>
              <FileText className='text-gray-500' size={20} />
            </div>
            <p className='text-2xl font-bold mb-1'>
              {formatCurrency(financialSummary.totalCost)}
            </p>
            <p className='text-xs text-gray-500'>
              {costBreakdown.length > 0 &&
                `${costBreakdown.length} cost components`}
            </p>
          </div>

          <div className='bg-white p-4 rounded-lg shadow'>
            <div className='flex justify-between items-start mb-2'>
              <h3 className='text-gray-500 text-sm'>Profit</h3>
              {financialSummary.profit >= 0 ? (
                <TrendingUp className='text-green-500' size={20} />
              ) : (
                <TrendingDown className='text-red-500' size={20} />
              )}
            </div>
            <p
              className={`text-2xl font-bold mb-1 ${
                financialSummary.profit >= 0 ? "text-green-600" : "text-red-600"
              }`}>
              {formatCurrency(financialSummary.profit)}
            </p>
            <p
              className={`text-xs ${
                financialSummary.profitMargin >= 15
                  ? "text-green-500"
                  : financialSummary.profitMargin >= 0
                  ? "text-yellow-500"
                  : "text-red-500"
              }`}>
              Margin: {financialSummary.profitMargin.toFixed(2)}%
            </p>
          </div>

          <div className='bg-white p-4 rounded-lg shadow'>
            <div className='flex justify-between items-start mb-2'>
              <h3 className='text-gray-500 text-sm'>Labor</h3>
              <Clock className='text-indigo-500' size={20} />
            </div>
            <p className='text-2xl font-bold mb-1'>
              {laborSummary?.totalHours
                ? `${laborSummary.totalHours.toFixed(2)} hrs`
                : "N/A"}
            </p>
            <p className='text-xs text-gray-500'>
              {laborSummary?.details
                ? `${laborSummary.details.length} entries`
                : "No time entries"}
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
          {/* Left Column - Cost Breakdown */}
          <div className='bg-white rounded-lg shadow lg:col-span-2'>
            <div className='p-4 border-b'>
              <h2 className='text-lg font-semibold'>Cost Breakdown</h2>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6 p-6'>
              {/* Cost Breakdown Pie Chart */}
              <div className='h-80'>
                <ResponsiveContainer width='100%' height='100%'>
                  <RechartPie>
                    <Pie
                      data={costBreakdown}
                      cx='50%'
                      cy='50%'
                      labelLine={false}
                      outerRadius={80}
                      fill='#8884d8'
                      dataKey='value'
                      nameKey='category'
                      label={({ category, percent }) =>
                        `${category}: ${(percent * 100).toFixed(0)}%`
                      }>
                      {costBreakdown.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                  </RechartPie>
                </ResponsiveContainer>
              </div>

              {/* Cost Breakdown Details */}
              <div>
                <div className='mb-6'>
                  <h3 className='text-md font-medium mb-3'>Cost Components</h3>
                  <ul className='space-y-3'>
                    {costBreakdown.map((item, index) => (
                      <li
                        key={index}
                        className='flex justify-between items-center p-3 bg-gray-50 rounded-md'>
                        <div className='flex items-center'>
                          <div
                            className='w-3 h-3 rounded-full mr-2'
                            style={{
                              backgroundColor: COLORS[index % COLORS.length],
                            }}></div>
                          <span>{item.category}</span>
                        </div>
                        <div className='text-right'>
                          <p className='font-medium'>
                            {formatCurrency(item.value)}
                          </p>
                          <p className='text-xs text-gray-500'>
                            {item.percentage.toFixed(1)}%
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className='text-md font-medium mb-3'>Cost Efficiency</h3>
                  <div className='space-y-2'>
                    <div className='p-3 bg-gray-50 rounded-md'>
                      <div className='flex justify-between'>
                        <span className='text-gray-600'>Cost per Unit</span>
                        <span className='font-medium'>
                          {projectData.Menge
                            ? formatCurrency(
                                financialSummary.totalCost /
                                  parseFloat(projectData.Menge),
                              )
                            : "N/A"}
                        </span>
                      </div>
                    </div>
                    <div className='p-3 bg-gray-50 rounded-md'>
                      <div className='flex justify-between'>
                        <span className='text-gray-600'>Labor Cost Rate</span>
                        <span className='font-medium'>
                          {laborSummary && laborSummary.totalHours > 0
                            ? formatCurrency(laborSummary.averageHourlyCost)
                            : "N/A"}
                          {laborSummary && laborSummary.totalHours > 0 && "/hr"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Labor Details */}
          <div className='bg-white rounded-lg shadow'>
            <div className='p-4 border-b'>
              <h2 className='text-lg font-semibold'>Labor Distribution</h2>
            </div>

            <div className='p-6'>
              {laborSummary &&
              laborSummary.details &&
              laborSummary.details.length > 0 ? (
                <>
                  {/* Labor Chart */}
                  <div className='mb-6 h-48'>
                    <ResponsiveContainer width='100%' height='100%'>
                      <BarChart data={laborChartData}>
                        <CartesianGrid strokeDasharray='3 3' />
                        <XAxis dataKey='date' />
                        <YAxis />
                        <Tooltip
                          formatter={(value, name) => [
                            name === "hours"
                              ? `${value.toFixed(2)} hours`
                              : formatCurrency(value),
                            name === "hours" ? "Hours" : "Cost",
                          ]}
                        />
                        <Bar dataKey='hours' name='Hours' fill='#8884d8' />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Labor Entries */}
                  <div>
                    <h3 className='text-md font-medium mb-3'>Time Entries</h3>
                    <div className='max-h-96 overflow-y-auto'>
                      <ul className='space-y-3'>
                        {laborSummary.details
                          .slice(0, 10)
                          .map((entry, index) => (
                            <li
                              key={index}
                              className='p-3 bg-gray-50 rounded-md'>
                              <div className='flex justify-between'>
                                <span className='font-medium'>
                                  {entry.user || "Unknown"}
                                </span>
                                <span className='text-gray-500'>
                                  {entry.duration.toFixed(2)} hrs
                                </span>
                              </div>
                              <p className='text-sm text-gray-600 my-1'>
                                {entry.description || "No description"}
                              </p>
                              <div className='flex justify-between text-xs text-gray-500'>
                                <span>
                                  {new Date(
                                    entry.startTime,
                                  ).toLocaleDateString()}{" "}
                                  -{" "}
                                  {new Date(entry.endTime).toLocaleDateString()}
                                </span>
                                <span className='font-medium'>
                                  {formatCurrency(entry.cost)}
                                </span>
                              </div>
                            </li>
                          ))}
                      </ul>

                      {laborSummary.details.length > 10 && (
                        <div className='text-center mt-4'>
                          <p className='text-sm text-gray-500'>
                            Showing 10 of {laborSummary.details.length} entries
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className='flex flex-col items-center justify-center h-64 text-center'>
                  <Clock className='text-gray-300 mb-4' size={48} />
                  <p className='text-gray-500 mb-2'>No labor data available</p>
                  <p className='text-sm text-gray-400'>
                    There are no time entries recorded for this project.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Financial Comparison Section */}
        <div className='bg-white rounded-lg shadow mt-6'>
          <div className='p-4 border-b'>
            <h2 className='text-lg font-semibold'>Financial Benchmarks</h2>
          </div>

          <div className='p-6'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              {/* Actual vs. Estimated Comparison */}
              <div>
                <h3 className='text-md font-medium mb-3'>
                  Actual vs. Estimated
                </h3>
                <div className='space-y-3'>
                  <div className='p-3 bg-gray-50 rounded-md'>
                    <div className='flex justify-between mb-1'>
                      <span className='text-gray-600'>Total Cost</span>
                      <div className='text-right'>
                        <span className='font-medium'>
                          {formatCurrency(financialSummary.totalCost)}
                        </span>
                        {projectData.EstimatedCost && (
                          <span
                            className={`text-xs ml-2 ${
                              financialSummary.totalCost <=
                              projectData.EstimatedCost
                                ? "text-green-500"
                                : "text-red-500"
                            }`}>
                            {financialSummary.totalCost <=
                            projectData.EstimatedCost
                              ? "✓ Under budget"
                              : "✗ Over budget"}
                          </span>
                        )}
                      </div>
                    </div>
                    {projectData.EstimatedCost && (
                      <div className='flex items-center mt-1'>
                        <div className='w-full bg-gray-200 rounded-full h-2.5'>
                          <div
                            className={`h-2.5 rounded-full ${
                              financialSummary.totalCost <=
                              projectData.EstimatedCost
                                ? "bg-green-500"
                                : "bg-red-500"
                            }`}
                            style={{
                              width: `${Math.min(
                                (financialSummary.totalCost /
                                  projectData.EstimatedCost) *
                                  100,
                                100,
                              )}%`,
                            }}></div>
                        </div>
                        <span className='text-xs text-gray-500 ml-2'>
                          {formatCurrency(projectData.EstimatedCost)} est.
                        </span>
                      </div>
                    )}
                  </div>

                  <div className='p-3 bg-gray-50 rounded-md'>
                    <div className='flex justify-between mb-1'>
                      <span className='text-gray-600'>Labor Hours</span>
                      <div className='text-right'>
                        <span className='font-medium'>
                          {laborSummary?.totalHours
                            ? `${laborSummary.totalHours.toFixed(2)} hrs`
                            : "N/A"}
                        </span>
                        {laborSummary?.totalHours &&
                          projectData.EstimatedHours && (
                            <span
                              className={`text-xs ml-2 ${
                                laborSummary.totalHours <=
                                projectData.EstimatedHours
                                  ? "text-green-500"
                                  : "text-red-500"
                              }`}>
                              {laborSummary.totalHours <=
                              projectData.EstimatedHours
                                ? "✓ Under estimate"
                                : "✗ Over estimate"}
                            </span>
                          )}
                      </div>
                    </div>
                    {laborSummary?.totalHours && projectData.EstimatedHours && (
                      <div className='flex items-center mt-1'>
                        <div className='w-full bg-gray-200 rounded-full h-2.5'>
                          <div
                            className={`h-2.5 rounded-full ${
                              laborSummary.totalHours <=
                              projectData.EstimatedHours
                                ? "bg-green-500"
                                : "bg-red-500"
                            }`}
                            style={{
                              width: `${Math.min(
                                (laborSummary.totalHours /
                                  projectData.EstimatedHours) *
                                  100,
                                100,
                              )}%`,
                            }}></div>
                        </div>
                        <span className='text-xs text-gray-500 ml-2'>
                          {projectData.EstimatedHours} hrs est.
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Profit Analysis */}
              <div>
                <h3 className='text-md font-medium mb-3'>Profit Analysis</h3>
                <div className='space-y-3'>
                  <div className='p-3 bg-gray-50 rounded-md'>
                    <div className='flex justify-between mb-1'>
                      <span className='text-gray-600'>Profit Margin</span>
                      <span
                        className={`font-medium ${
                          financialSummary.profitMargin >= 20
                            ? "text-green-600"
                            : financialSummary.profitMargin >= 10
                            ? "text-yellow-600"
                            : "text-red-600"
                        }`}>
                        {financialSummary.profitMargin.toFixed(2)}%
                      </span>
                    </div>
                    <div className='flex items-center mt-1'>
                      <div className='w-full bg-gray-200 rounded-full h-2.5'>
                        <div
                          className={`h-2.5 rounded-full ${
                            financialSummary.profitMargin >= 20
                              ? "bg-green-500"
                              : financialSummary.profitMargin >= 10
                              ? "bg-yellow-500"
                              : financialSummary.profitMargin >= 0
                              ? "bg-orange-500"
                              : "bg-red-500"
                          }`}
                          style={{
                            width: `${
                              Math.max(
                                Math.min(financialSummary.profitMargin, 30),
                                0,
                              ) *
                              (100 / 30)
                            }%`,
                          }}></div>
                      </div>
                      <span className='text-xs text-gray-500 ml-2'>
                        Target: 20%
                      </span>
                    </div>
                  </div>

                  <div className='p-3 bg-gray-50 rounded-md'>
                    <div className='flex justify-between'>
                      <span className='text-gray-600'>
                        Revenue per Labor Hour
                      </span>
                      <span className='font-medium'>
                        {laborSummary?.totalHours && laborSummary.totalHours > 0
                          ? formatCurrency(
                              financialSummary.revenue /
                                laborSummary.totalHours,
                            )
                          : "N/A"}
                      </span>
                    </div>
                  </div>

                  <div className='p-3 bg-gray-50 rounded-md'>
                    <div className='flex justify-between'>
                      <span className='text-gray-600'>Completion Status</span>
                      <span className='font-medium'>
                        {projectData.percentDone || "0"}%
                      </span>
                    </div>
                    <div className='w-full bg-gray-200 rounded-full h-2.5 mt-2'>
                      <div
                        className='h-2.5 rounded-full bg-blue-500'
                        style={{
                          width: `${projectData.percentDone || 0}%`,
                        }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Actions Footer */}
        <div className='flex justify-between mt-6'>
          <button
            onClick={() => navigate(`/projects/${id}`)}
            className='flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200'>
            <FileText size={18} className='mr-2' />
            View Project Details
          </button>

          <button
            onClick={() => navigate(`/projects/edit/${id}`)}
            className='flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700'>
            <DollarSign size={18} className='mr-2' />
            Update Financial Data
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectFinancialDetails;
